// Case Summary Stats Component
// BP-10-04: Attorney Frontend

import {
  Briefcase,
  Clock,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Calendar,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';

interface DashboardStats {
  totalCases: number;
  activeCases: number;
  casesInDiscovery: number;
  casesInSettlement: number;
  closedCasesThisYear: number;
  totalSettlementsThisYear: number;
  averageSettlement: number;
  upcomingDeadlines: number;
  clientEngagementRate: number;
}

interface CaseSummaryStatsProps {
  stats: DashboardStats;
  isLoading?: boolean;
}

export function CaseSummaryStats({ stats, isLoading = false }: CaseSummaryStatsProps) {
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  const statCards = [
    {
      label: 'Active Cases',
      value: stats.activeCases,
      icon: Briefcase,
      color: 'text-sage bg-sage/10',
      description: `${stats.totalCases} total cases`,
    },
    {
      label: 'In Discovery',
      value: stats.casesInDiscovery,
      icon: Clock,
      color: 'text-amber-600 bg-amber-50',
      description: 'Pending discovery',
    },
    {
      label: 'Settlement Phase',
      value: stats.casesInSettlement,
      icon: DollarSign,
      color: 'text-green-600 bg-green-50',
      description: 'Ready to settle',
    },
    {
      label: 'Avg Settlement',
      value: formatCurrency(stats.averageSettlement),
      icon: TrendingUp,
      color: 'text-blue-600 bg-blue-50',
      isFormatted: true,
      description: `${stats.closedCasesThisYear} closed this year`,
    },
    {
      label: 'Upcoming Deadlines',
      value: stats.upcomingDeadlines,
      icon: AlertTriangle,
      color: stats.upcomingDeadlines > 5 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-100',
      description: 'Next 30 days',
    },
    {
      label: 'Client Engagement',
      value: `${stats.clientEngagementRate}%`,
      icon: CheckCircle,
      color: 'text-sage bg-sage/10',
      isFormatted: true,
      description: 'Active clients using portal',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse"
          >
            <div className="w-10 h-10 bg-gray-200 rounded-lg mb-3" />
            <div className="h-6 bg-gray-200 rounded w-16 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {statCards.map((stat, index) => (
        <div
          key={index}
          className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-colors"
        >
          <div
            className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center mb-3',
              stat.color
            )}
          >
            <stat.icon className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-navy mb-1">
            {stat.isFormatted ? stat.value : stat.value.toLocaleString()}
          </div>
          <div className="text-sm font-medium text-gray-700">{stat.label}</div>
          {stat.description && (
            <div className="text-xs text-gray-500 mt-1">{stat.description}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Compact version for case detail pages
interface CaseQuickStatsProps {
  case: {
    status: string;
    estimatedValue?: number;
    settlementAmount?: number;
    filedDate?: string;
    incidentDate?: string;
  };
}

export function CaseQuickStats({ case: caseData }: CaseQuickStatsProps) {
  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="text-xs text-gray-500 mb-1">Incident Date</div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-navy">
            {formatDate(caseData.incidentDate)}
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <div className="text-xs text-gray-500 mb-1">Filed Date</div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-navy">
            {formatDate(caseData.filedDate)}
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <div className="text-xs text-gray-500 mb-1">Estimated Value</div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-navy">
            {formatCurrency(caseData.estimatedValue)}
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <div className="text-xs text-gray-500 mb-1">Settlement</div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-sage" />
          <span className="text-sm font-medium text-sage">
            {formatCurrency(caseData.settlementAmount)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default CaseSummaryStats;
